'use client';

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';
import { VideoMediaSource } from '../../types';
import { classNames, convertButterCmsToRobloxUrl, ROBLOX_CMS_CDN_URL } from '../../utils';

type PlaybackMode = 'autoplay' | 'controls' | undefined;
const DEFAULT_POSTER_WIDTH = 1280;

function getPayloadCdnBase(): string | undefined {
  if (typeof process !== 'undefined') {
    return process.env?.NEXT_PUBLIC_CMS_MEDIA;
  }

  return undefined;
}

function getOptimizedPosterUrl(url: string): string {
  if (!url.trim()) return url;

  // Already has resize params — nothing to do
  if (url.startsWith(`${ROBLOX_CMS_CDN_URL}resize=`)) return url;

  // Payload CMS CloudFront URLs are served as-is — skip resize
  const payloadCdnBase = getPayloadCdnBase();
  if (payloadCdnBase && url.startsWith(payloadCdnBase)) return url;

  if (url.startsWith(ROBLOX_CMS_CDN_URL)) {
    const fileId = url.split(ROBLOX_CMS_CDN_URL)[1]?.split('/').pop();
    if (!fileId) return url;
    return `${ROBLOX_CMS_CDN_URL}resize=width:${DEFAULT_POSTER_WIDTH},fit:max/${fileId}`;
  }

  return url;
}

interface VideoProps extends Omit<React.VideoHTMLAttributes<HTMLVideoElement>, 'src'> {
  media: VideoMediaSource;
  mode?: PlaybackMode;
  className?: string;
  preload?: 'auto' | 'metadata' | 'none';
  fetchPriority?: 'high' | 'low' | 'auto';
  lazyLoad?: boolean;
}

export const Video = ({
  media,
  mode = 'autoplay',
  preload = 'metadata',
  fetchPriority,
  className,
  lazyLoad = true,
  ...props
}: VideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { ref: inViewRef, inView } = useInView({
    threshold: 0,
    rootMargin: '100px',
    triggerOnce: true,
  });

  const shouldLoad = !lazyLoad || inView;

  const transformedSrc = convertButterCmsToRobloxUrl(media.src);
  const transformedPoster =
    media.poster && media.poster.trim() !== ''
      ? getOptimizedPosterUrl(convertButterCmsToRobloxUrl(media.poster))
      : undefined;

  useEffect(() => {
    if (shouldLoad && videoRef.current) {
      videoRef.current.load();
    }
  }, [shouldLoad]);

  const isAutoplay = mode === 'autoplay';
  const videoProps = {
    ref: videoRef,
    poster: transformedPoster,
    preload: shouldLoad ? preload : 'none',
    ...(fetchPriority ? { fetchpriority: fetchPriority } : {}),
    className: classNames('video', className),
    autoPlay: isAutoplay,
    muted: isAutoplay,
    loop: isAutoplay,
    playsInline: true,
    controls: !isAutoplay,
    ...props,
  };

  return (
    <div ref={inViewRef} className="video-wrapper">
      <video {...videoProps}>
        {shouldLoad && transformedSrc?.trim() && <source src={transformedSrc} type="video/mp4" />}
      </video>
    </div>
  );
};

Video.propTypes = {
  media: PropTypes.shape({
    src: PropTypes.string.isRequired,
    poster: PropTypes.string,
    autoPlay: PropTypes.bool,
    muted: PropTypes.bool,
    loop: PropTypes.bool,
    videoTitle: PropTypes.string,
    caption: PropTypes.string,
  }).isRequired,
  mode: PropTypes.oneOf(['autoplay', 'controls']),
  preload: PropTypes.oneOf(['auto', 'metadata', 'none']),
  fetchPriority: PropTypes.oneOf(['high', 'low', 'auto']),
  lazyLoad: PropTypes.bool,
  className: PropTypes.string,
};
